class PriceHistogram {
  constructor(container) {
    this.container = container;
    this.min = parseFloat(container.dataset.min) || 0;
    this.max = parseFloat(container.dataset.max) || 1;
    this.collectionUrl = container.dataset.collectionUrl || '';
    this.bucketCount = 10;
    this.svgWidth = 120;
    this.svgHeight = 20;

    this.loadAndRender();
  }

  async loadAndRender() {
    let prices = [];

    try {
      prices = await this.fetchPrices();
    } catch (e) {
      
      prices = [];
    }

    this.render(prices);
  }

  async fetchPrices() {
    if (!this.collectionUrl) return [];

    const url = `${this.collectionUrl}/products.json?limit=250`;
    const response = await fetch(url);

    if (!response.ok) return [];

    const data = await response.json();
    const prices = [];

    data.products.forEach((product) => {
      product.variants.forEach((variant) => {
        const price = parseFloat(variant.price);
        if (!isNaN(price)) {
          prices.push(price);
        }
      });
    });

    return prices;
  }

  getBuckets(prices) {
    const buckets = new Array(this.bucketCount).fill(0);

    if (prices.length === 0) return buckets;

    const range = this.max - this.min;
    if (range <= 0) return buckets;

    const bucketSize = range / this.bucketCount;

    prices.forEach((price) => {
      const rounded = Math.round(price);
      if (rounded >= this.min && rounded <= this.max) {
        const idx = Math.min(
          Math.floor((rounded - this.min) / bucketSize),
          this.bucketCount - 1
        );
        buckets[idx]++;
      }
    });

    return buckets;
  }

  toLevel(count, maxCount) {
    if (count === 0) return 1;
    
    
    const minLevel = 4;
    const ratio = count / maxCount;
    const raw = Math.ceil(Math.log10(1 + ratio * 9) / Math.log10(10) * 10);
    return minLevel + Math.round((raw - 1) / 9 * (10 - minLevel));
  }

  render(prices) {
    const buckets = this.getBuckets(prices);
    const maxCount = Math.max(...buckets, 1);
    const levels = 10;
    const w = this.svgWidth;
    const h = this.svgHeight;

    
    const points = buckets.map((count, i) => {
      const x = (i / (this.bucketCount - 1)) * w;
      const level = this.toLevel(count, maxCount);
      const y = h - (level / levels) * h;
      return { x, y };
    });

    
    const polygonPointsStr = [
      `0,${h}`,
      ...points.map((p) => `${p.x},${p.y}`),
      `${w},${h}`,
    ].join(' ');

    const ns = 'http://www.w3.org/2000/svg';
    const svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
    svg.setAttribute('preserveAspectRatio', 'none');
    svg.setAttribute('aria-hidden', 'true');

    const polygon = document.createElementNS(ns, 'polygon');
    polygon.setAttribute('points', polygonPointsStr);
    polygon.setAttribute('class', 'inline-histogram__fill');

    svg.appendChild(polygon);

    this.container.innerHTML = '';
    this.container.appendChild(svg);
  }
}

class InlineFilterPanel {
  constructor() {
    this.panel = document.getElementById('inline-filter-panel');
    this.overlay = document.getElementById('inline-filter-overlay');
    this.toggleButtons = document.querySelectorAll('[data-inline-filter-toggle]');
    this.closeButton = this.panel ? this.panel.querySelector('[data-inline-filter-close]') : null;

    if (!this.panel) return;

    this.bindEvents();
    this.initHistograms();
  }

  bindEvents() {
    this.toggleButtons.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle();
      });
    });

    if (this.closeButton) {
      this.closeButton.addEventListener('click', () => this.close());
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.panel.classList.contains('is-open')) {
        this.close();
        if (this.toggleButtons[0]) this.toggleButtons[0].focus();
      }
    });
  }

  toggle() {
    if (this.panel.classList.contains('is-open')) {
      this.close();
    } else {
      this.open();
    }
  }

  open() {
    // Only one of Filter / Sort open at a time.
    if (window._sortBarInstance) window._sortBarInstance.close();
    this.panel.classList.add('is-open');
    // `inert` alongside aria-hidden, deliberately. Both of these panels collapse with
    // `grid-template-rows: 0fr` + `overflow: hidden` — there is no display:none and no
    // visibility:hidden on desktop, so every control inside (the close button, ~18 filter
    // inputs / the role="button" sort options) stayed in the tab order while the wrapper
    // claimed aria-hidden="true". That is axe aria-hidden-focus, and it is the DEFAULT
    // closed state, i.e. what every desktop visitor and every audit sees on load.
    // inert removes focusability and pointer events without touching layout, so the
    // collapse animation is unaffected; browsers without inert simply behave as before.
    this.panel.setAttribute('aria-hidden', 'false');
    this.panel.removeAttribute('inert');
    if (this.overlay) this.overlay.classList.add('is-open');
    if (window.matchMedia('(max-width: 989px)').matches) {
      
      
      this._preventBodyScroll = (e) => {
        if (!this.panel.contains(e.target)) {
          e.preventDefault();
        }
      };
      document.addEventListener('touchmove', this._preventBodyScroll, { passive: false });
    }
    this.toggleButtons.forEach((btn) => {
      btn.classList.add('is-open');
      btn.setAttribute('aria-expanded', 'true');
    });
  }

  close() {
    this.panel.classList.remove('is-open');
    this.panel.setAttribute('aria-hidden', 'true');
    this.panel.setAttribute('inert', '');
    if (this.overlay) this.overlay.classList.remove('is-open');
    if (this._preventBodyScroll) {
      document.removeEventListener('touchmove', this._preventBodyScroll);
      this._preventBodyScroll = null;
    }
    this.toggleButtons.forEach((btn) => {
      btn.classList.remove('is-open');
      btn.setAttribute('aria-expanded', 'false');
    });
  }

  initHistograms() {
    this.panel.querySelectorAll('.inline-histogram').forEach((el) => {
      new PriceHistogram(el);
    });
  }

}

const reinitInlineFilterPanel = () => {
  const panel = document.getElementById('inline-filter-panel');
  if (!panel) return;

  panel.querySelectorAll('.inline-histogram').forEach((el) => {
    if (!el.querySelector('svg')) {
      new PriceHistogram(el);
    }
  });
};

// AAS-1069: the compare feature is gated behind the theme setting
// "Feature flags → Enable product compare". This class only instantiates when a
// [data-compare-toggle] button exists in the DOM, and that button is only
// rendered when the setting is on — so when compare is off nothing runs.
class CompareMode {
  constructor() {
    this.MAX_ITEMS = 2;
    this.toggleBtn = document.querySelector('[data-compare-toggle]');
    this.bar = document.getElementById('compare-bar');
    this.barCount = document.getElementById('compare-bar-count');
    this.barContext = document.getElementById('compare-bar-context');
    this.barCta = document.getElementById('compare-bar-cta');
    this.barSlots = document.getElementById('compare-bar-slots');
    this.clearBtn = document.querySelector('[data-compare-clear]');

    this.selected = [];
    this.isActive = false;

    // Comparison is restricted to a single product category (product.type).
    // The first selected product locks the category for the rest of the session.
    this.activeType = null;

    // Translated UI strings provided by the compare-bar markup (locale-aware).
    this.strings = {
      context: this.bar?.dataset.compareContextLabel || 'Comparing:',
      incompatible: this.bar?.dataset.compareIncompatibleLabel || 'Different category',
      mismatchTitle: this.bar?.dataset.compareMismatchTitle || 'Compare a different category?',
      mismatchBody: this.bar?.dataset.compareMismatchBody || 'You can only compare products of the same type. Start a new comparison with this one?',
      mismatchConfirm: this.bar?.dataset.compareMismatchConfirm || 'Start new comparison',
      mismatchCancel: this.bar?.dataset.compareMismatchCancel || 'Keep current',
    };

    if (!this.toggleBtn) return;

    this.bindEvents();
  }

  bindEvents() {
    this.toggleBtn.addEventListener('click', () => this.toggleMode());

    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-compare-btn]');
      if (btn) this.toggleProduct(btn);
    });

    if (this.clearBtn) {
      this.clearBtn.addEventListener('click', () => this.clearAll());
    }

    document.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('[data-compare-slot-remove]');
      if (removeBtn) {
        const slot = parseInt(removeBtn.dataset.compareSlotRemove, 10) - 1;
        if (this.selected[slot]) this.deselect(this.selected[slot].id);
      }
    });
  }

  toggleMode() {
    this.isActive = !this.isActive;
    document.body.classList.toggle('compare-mode-active', this.isActive);
    this.toggleBtn.setAttribute('aria-pressed', String(this.isActive));

    if (!this.isActive) {
      this.clearAll();
    } else {
      this.updateBar();
    }
  }

  // Single source of truth for the comparison "category" (product.type, set by
  // PIM via the Salsify taxonomy). Normalised so minor inconsistencies don't
  // split a category: lower-cased, trimmed, and singular/plural folded so legacy
  // products ("Alpine Ski" vs "Alpine Skis") still compare correctly.
  categoryKey(type) {
    return (type || '')
      .trim()
      .toLowerCase()
      .split(/\s+/)
      .map((word) => word.replace(/s$/, ''))
      .join(' ');
  }

  _btnById(id) {
    return document.querySelector(`[data-compare-btn][data-product-id="${id}"]`);
  }

  toggleProduct(btn) {
    if (!this.isActive) return;

    const id = btn.dataset.productId;
    const key = this.categoryKey(btn.dataset.productType);
    const isSelected = btn.getAttribute('aria-pressed') === 'true';

    if (isSelected) {
      this.deselect(id);
      return;
    }

    // A comparison is in progress and this is a different category → offer to
    // start over instead of silently blocking or mixing unrelated products.
    // Keyed on "is a comparison active", not on a truthy type, so products with
    // no type form their own bucket rather than acting as a wildcard.
    if (this.selected.length > 0 && key !== this.activeType) {
      this.handleMismatch(btn);
      return;
    }

    if (this.selected.length < this.MAX_ITEMS) {
      this.select(btn);
    } else {
      this.shakeBar();
    }
  }

  select(btn) {
    const item = {
      id: btn.dataset.productId,
      type: this.categoryKey(btn.dataset.productType),
      title: btn.dataset.productTitle,
      url: btn.dataset.productUrl,
      image: btn.dataset.productImage,
      btn,
    };
    this.selected.push(item);
    // First pick locks the category (even an empty one, for typeless products).
    if (this.selected.length === 1) this.activeType = item.type;
    btn.setAttribute('aria-pressed', 'true');
    this.updateBar();
  }

  deselect(id) {
    if (!this.selected.some((p) => p.id === id)) return;

    this._btnById(id)?.setAttribute('aria-pressed', 'false');
    this.selected = this.selected.filter((p) => p.id !== id);
    if (this.selected.length === 0) this.activeType = null;
    this.updateBar();
  }

  clearAll() {
    this.selected.forEach((item) => this._btnById(item.id)?.setAttribute('aria-pressed', 'false'));
    this.selected = [];
    this.activeType = null;
    this.updateBar();
  }

  // Re-link the selection to freshly rendered cards after the grid re-renders
  // (filtering / "load more"), restoring pressed state and the group dimming.
  onGridUpdated() {
    this.selected.forEach((item) => {
      const btn = this._btnById(item.id);
      if (btn) {
        item.btn = btn;
        btn.setAttribute('aria-pressed', 'true');
      }
    });
    this.updateCardStates();
  }

  // A mismatched pick offers a clear choice rather than a dead/disabled control:
  // start a fresh comparison with the new product, or keep the current set.
  handleMismatch(btn) {
    this.openDialog({
      onConfirm: () => {
        this.clearAll();
        this.select(btn);
      },
    });
  }

  updateBar() {
    if (!this.bar) return;

    const count = this.selected.length;

    if (count === 0) {
      this.bar.hidden = true;
    } else {
      this.bar.hidden = false;
      this.bar.removeAttribute('hidden');
    }

    if (this.barCount) {
      const max = this.MAX_ITEMS;
      this.barCount.textContent = `${count}/${max}`;
    }

    if (this.barContext) {
      if (this.activeType) {
        this.barContext.textContent = `${this.strings.context} ${this.activeType}`;
        this.barContext.hidden = false;
      } else {
        this.barContext.textContent = '';
        this.barContext.hidden = true;
      }
    }

    this.updateCardStates();

    if (this.barCta) {
      const canCompare = count >= 2;
      this.barCta.setAttribute('aria-disabled', String(!canCompare));

      if (canCompare) {
        const handles = this.selected.map((p) => {
          const match = p.url?.match(/\/products\/([^/?#]+)/);
          return match ? match[1] : p.id;
        });
        const baseUrl = this.barCta.dataset.compareUrl || this.barCta.getAttribute('href')?.split('?')[0] || '/pages/compare';
        const collection = this.bar?.dataset.collection || '';
        const collectionParam = collection ? `&collection=${collection}` : '';
        this.barCta.href = `${baseUrl}?products=${handles.join(',')}${collectionParam}`;
      }
    }

    if (this.barSlots) {
      this.barSlots.querySelectorAll('[data-compare-slot]').forEach((slot, i) => {
        const item = this.selected[i];
        slot.classList.toggle('is-filled', Boolean(item));

        const img = slot.querySelector('.compare-bar__slot-image');
        if (img) {
          img.src = item ? item.image : '';
          img.alt = item ? item.title : '';
        }
      });
    }
  }

  shakeBar() {
    if (!this.bar) return;
    this.bar.classList.add('compare-bar--shake');
    setTimeout(() => this.bar.classList.remove('compare-bar--shake'), 400);
  }

  // Dim compare buttons of a different category as a group (explained by the
  // bar context label) — they stay clickable so the mismatch dialog can fire.
  updateCardStates() {
    const hasSelection = this.selected.length > 0;
    document.querySelectorAll('[data-compare-btn]').forEach((btn) => {
      const key = this.categoryKey(btn.dataset.productType);
      const isSelected = btn.getAttribute('aria-pressed') === 'true';
      const incompatible = hasSelection && !isSelected && key !== this.activeType;
      btn.classList.toggle('is-incompatible', incompatible);
      if (incompatible) {
        btn.setAttribute('data-compare-incompatible', this.strings.incompatible);
      } else {
        btn.removeAttribute('data-compare-incompatible');
      }
    });
  }

  openDialog({ onConfirm }) {
    this.closeDialog();

    const dialog = document.createElement('div');
    dialog.className = 'compare-dialog';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-label', this.strings.mismatchTitle);
    dialog.innerHTML = `
      <div class="compare-dialog__backdrop" data-compare-dialog-cancel></div>
      <div class="compare-dialog__panel">
        <p class="compare-dialog__title">${this.strings.mismatchTitle}</p>
        <p class="compare-dialog__body">${this.strings.mismatchBody}</p>
        <div class="compare-dialog__actions">
          <button type="button" class="compare-dialog__cancel" data-compare-dialog-cancel>${this.strings.mismatchCancel}</button>
          <button type="button" class="compare-dialog__confirm btn btn-primary" data-compare-dialog-confirm>${this.strings.mismatchConfirm}</button>
        </div>
      </div>
    `;

    dialog.querySelector('[data-compare-dialog-confirm]').addEventListener('click', () => {
      this.closeDialog();
      onConfirm();
    });
    dialog.querySelectorAll('[data-compare-dialog-cancel]').forEach((el) => {
      el.addEventListener('click', () => this.closeDialog());
    });

    this._dialogKeyHandler = (e) => { if (e.key === 'Escape') this.closeDialog(); };
    document.addEventListener('keydown', this._dialogKeyHandler);

    document.body.appendChild(dialog);
    this.dialog = dialog;
    dialog.querySelector('[data-compare-dialog-confirm]')?.focus();
  }

  closeDialog() {
    if (this._dialogKeyHandler) {
      document.removeEventListener('keydown', this._dialogKeyHandler);
      this._dialogKeyHandler = null;
    }
    if (this.dialog) {
      this.dialog.remove();
      this.dialog = null;
    }
  }
}

const reinitCompareMode = () => {
  if (window._compareModeInstance) return;
  // AAS-1069: gated behind the "Enable product compare" theme setting. No
  // [data-compare-toggle] is rendered while compare is off, so CompareMode never
  // instantiates here.
  const btn = document.querySelector('[data-compare-toggle]');
  if (btn) window._compareModeInstance = new CompareMode();
};

/**
 * Sort-as-filters (Figma 5098-113282). The Sort toolbar button toggles the
 * inline sort row; selecting any sort option (toolbar or mobile drawer) swaps
 * the ?sort_by= param while preserving active filters via a full navigation.
 */
class SortBar {
  constructor() {
    this.bar = document.getElementById('inline-sort-bar');
    this.bindToggle();
    this.bindNavigation();
  }

  bindToggle() {
    this.toggleButtons = document.querySelectorAll('[data-sort-toggle]');
    if (!this.toggleButtons.length || !this.bar) return;
    this.overlay = document.getElementById('sort-bar-overlay');
    this.closeButton = this.bar.querySelector('[data-sort-close]');
    this.toggleButtons.forEach((btn) => {
      if (btn.dataset.sortBound) return;
      btn.dataset.sortBound = 'true';
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.toggle();
      });
    });
    if (this.closeButton && !this.closeButton.dataset.sortBound) {
      this.closeButton.dataset.sortBound = 'true';
      this.closeButton.addEventListener('click', () => this.close());
    }
    if (this.overlay && !this.overlay.dataset.sortBound) {
      this.overlay.dataset.sortBound = 'true';
      this.overlay.addEventListener('click', () => this.close());
    }
    if (!this._escBound) {
      this._escBound = true;
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.bar.classList.contains('is-open')) {
          this.close();
          if (this.toggleButtons[0]) this.toggleButtons[0].focus();
        }
      });
    }
  }

  toggle() {
    this.bar.classList.contains('is-open') ? this.close() : this.open();
  }

  open() {
    // Only one of Filter / Sort open at a time.
    if (window._inlineFilterPanel) window._inlineFilterPanel.close();
    this.bar.classList.add('is-open');
    this.bar.setAttribute('aria-hidden', 'false');
    this.bar.removeAttribute('inert');
    if (this.overlay) this.overlay.classList.add('is-open');
    // Lock background scroll while the mobile bottom sheet is open.
    if (window.matchMedia('(max-width: 989px)').matches) {
      this._preventBodyScroll = (e) => {
        if (!this.bar.contains(e.target)) e.preventDefault();
      };
      document.addEventListener('touchmove', this._preventBodyScroll, { passive: false });
    }
    this.toggleButtons.forEach((btn) => {
      btn.classList.add('is-open');
      btn.setAttribute('aria-expanded', 'true');
    });
  }

  close() {
    this.bar.classList.remove('is-open');
    this.bar.setAttribute('aria-hidden', 'true');
    this.bar.setAttribute('inert', '');
    if (this.overlay) this.overlay.classList.remove('is-open');
    if (this._preventBodyScroll) {
      document.removeEventListener('touchmove', this._preventBodyScroll);
      this._preventBodyScroll = null;
    }
    this.toggleButtons.forEach((btn) => {
      btn.classList.remove('is-open');
      btn.setAttribute('aria-expanded', 'false');
    });
  }

  // Re-sort in place via the same AJAX pipeline the filters use. The sort
  // options are non-navigating role="button" elements (no href), so there is
  // nothing to hard-navigate: if the AJAX pipeline somehow isn't available the
  // click is simply a no-op rather than a full page reload.
  applySort(trigger, event) {
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', trigger.dataset.sortValue);
    url.searchParams.delete('page');
    if (
      typeof FacetFiltersForm !== 'undefined' &&
      typeof FacetFiltersForm.renderPage === 'function' &&
      document.getElementById('ProductGridContainer')
    ) {
      FacetFiltersForm.renderPage(url.searchParams.toString(), event);
    }
  }

  // Bound once on the document so it survives facet AJAX re-renders.
  bindNavigation() {
    if (window._sortNavBound) return;
    window._sortNavBound = true;

    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-sort-value]');
      if (!trigger) return;
      e.preventDefault();
      this.applySort(trigger, e);
    });

    // role="button" spans aren't natively keyboard-activatable, so mirror the
    // Enter / Space behaviour a real button would give us.
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
      const trigger = e.target.closest('[data-sort-value]');
      if (!trigger) return;
      e.preventDefault();
      this.applySort(trigger, e);
    });
  }
}

const reinitSortBar = () => {
  if (window._sortBarInstance) {
    window._sortBarInstance.bindToggle();
    return;
  }
  window._sortBarInstance = new SortBar();
};

// Classic script: a section swap can re-evaluate this file. Bind once.
if (!window._inlineSizeShowMoreBound) {
  window._inlineSizeShowMoreBound = true;
  document.addEventListener('click', (event) => {
    const btn = event.target.closest('[data-inline-size-show-more]');
    if (!btn) return;
    event.preventDefault();
    const fieldset = btn.closest('.inline-facet-blocks--truncated');
    if (!fieldset) return;
    const expanded = fieldset.classList.toggle('is-expanded');
    btn.setAttribute('aria-expanded', String(expanded));
    const moreLabel = btn.querySelector('[data-inline-size-show-more-label="more"]');
    const lessLabel = btn.querySelector('[data-inline-size-show-more-label="less"]');
    if (moreLabel) moreLabel.hidden = expanded;
    if (lessLabel) lessLabel.hidden = !expanded;
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window._inlineFilterPanel = new InlineFilterPanel();
    reinitCompareMode();
    reinitSortBar();
  });
} else {
  window._inlineFilterPanel = new InlineFilterPanel();
  reinitCompareMode();
  reinitSortBar();
}

const _origReinit =
  typeof reinitializeCustomElements === 'function'
    ? reinitializeCustomElements
    : null;

if (typeof reinitializeCustomElements !== 'undefined') {
  const _prev = reinitializeCustomElements;
  reinitializeCustomElements = () => {
    _prev();
    reinitInlineFilterPanel();
    reinitCompareMode();
    // Re-apply compare selection/dimming to the freshly rendered grid.
    window._compareModeInstance?.onGridUpdated();
    reinitSortBar();
  };
}
